/**
 * Analytics Service
 * Centralized service for tracking user events. Sends to PostHog
 * (when configured).
 */

import posthog from 'posthog-js';

const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED !== 'false';
const ANALYTICS_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true' || process.env.NODE_ENV === 'development';

// Generate session ID (persists for browser session)
let sessionId = null;
function getSessionId() {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    try {
      sessionStorage.setItem('analytics_session_id', sessionId);
    } catch (e) {
      // sessionStorage not available, use in-memory only
    }
  }
  return sessionId;
}

// Try to restore session ID from sessionStorage
try {
  sessionId = sessionStorage.getItem('analytics_session_id') || null;
} catch (e) {
  // sessionStorage not available
}

function getEnvironment() {
  if (typeof window === 'undefined') return 'production';
  const hostname = window.location.hostname || '';
  if (hostname.includes('test.')) return 'test';
  if (hostname.includes('defenceframework')) return 'defence';
  return 'production';
}

/**
 * Track an event
 * @param {string} eventName - The event name (from analyticsEvents.EVENTS)
 * @param {Object} properties - Event-specific properties
 * @param {Object} user - User object from AppContext (optional)
 * @param {Object} organization - Organization object from AppContext (optional)
 * @param {string} path - Current page path (optional, auto-detected if not provided)
 */
export async function trackEvent(eventName, properties = {}, user = null, organization = null, path = null) {
  // Check if analytics is enabled
  if (!ANALYTICS_ENABLED) {
    if (ANALYTICS_DEBUG) {
      console.log('[Analytics] Tracking disabled, skipping event:', eventName);
    }
    return;
  }

  try {
    // Get current path if not provided
    if (!path && typeof window !== 'undefined') {
      path = window.location.pathname;
    }

    // Extract user identifiers (no PII)
    const userId = user?.id || user?.userId || null;
    const orgId = organization?.id || organization?.orgId || null;

    const environment = getEnvironment();

    // Build event payload
    const eventPayload = {
      event: eventName,
      properties: {
        ...properties,
        // Add common metadata
        sessionId: getSessionId(),
        path: path || null,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        environment,
      },
      userId: userId,
      orgId: orgId,
      timestamp: new Date().toISOString(),
    };

    if (ANALYTICS_DEBUG) {
      console.log('[Analytics] Tracking event:', eventName, eventPayload);
    }

    // Send to PostHog (when API key is configured and init ran in index.js)
    if (process.env.REACT_APP_POSTHOG_KEY && posthog.__loaded) {
      try {
        if (userId) {
          posthog.identify(userId);
        }
        posthog.capture(eventName, {
          ...properties,
          sessionId: getSessionId(),
          path: path || null,
          orgId: orgId || undefined,
          environment,
        });
      } catch (phError) {
        if (ANALYTICS_DEBUG) {
          console.error('[Analytics] PostHog capture error:', phError);
        }
      }
    }

    // PostHog is the only analytics sink (the CloudWatch backend is gone).
    if (ANALYTICS_DEBUG) {
      console.log('[Analytics] Event tracked successfully:', eventName);
    }
  } catch (error) {
    // Fail silently in production, log in debug mode
    if (ANALYTICS_DEBUG) {
      console.error('[Analytics] Error tracking event:', eventName, error);
    }
    // Don't throw - tracking failures should not affect user experience
  }
}

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {Object} user - User object from AppContext (optional)
 * @param {Object} organization - Organization object from AppContext (optional)
 * @param {string} component - Component name (optional)
 */
export async function trackPageView(path, user = null, organization = null, component = null) {
  const properties = {
    path,
    component: component || null,
  };

  // Extract search params if available
  if (typeof window !== 'undefined' && window.location.search) {
    try {
      const params = new URLSearchParams(window.location.search);
      const searchParams = {};
      for (const [key, value] of params.entries()) {
        searchParams[key] = value;
      }
      if (Object.keys(searchParams).length > 0) {
        properties.searchParams = searchParams;
      }
    } catch (e) {
      // Ignore search params parsing errors
    }
  }

  await trackEvent('page_viewed', properties, user, organization, path);
}

